// src/pages/Dashboard.tsx - Updated with deck system integration

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ddApi } from '../lib/supabaseClient';
import { Game, Card, TSOProfile, DeckChoice } from '../types';
import CardDisplay from '../components/CardDisplay';
import GameList from '../components/GameList';
import PlayerList from '../components/PlayerList';
import { useAuth } from '../context/AuthContext';
import CardShop from '../components/CardShop';
import Leaderboard from '../components/Leaderboard';

const Dashboard: React.FC = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
const [activeTab, setActiveTab] = useState<'games' | 'collection' | 'shop' | 'players' | 'leaderboard'>('games');
  const [games, setGames] = useState<Game[]>([]);
  const [playerCards, setPlayerCards] = useState<Card[]>([]);
  const [availablePlayers, setAvailablePlayers] = useState<TSOProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [gettingStarterPack, setGettingStarterPack] = useState(false);
const [starterPackMessage, setStarterPackMessage] = useState<{
  type: 'success' | 'error';
  text: string;
} | null>(null);

  useEffect(() => {
    if (!user) return;
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Load all dashboard data in parallel
      const [gamesData, cardsData, playersData] = await Promise.all([
        ddApi.getPlayerGames(user.id),
        ddApi.getPlayerCards(user.id),
        ddApi.getAvailablePlayers(user.id)
      ]);

      setGames(gamesData);
      setPlayerCards(cardsData.map(pc => pc.card!));
      setAvailablePlayers(playersData);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Updated: Create game with deck selection
  const handleCreateGame = async (opponentId: string, deckChoice: DeckChoice) => {
    if (!user) return;
    
    try {
      const newGame = await ddApi.createGame(user.id, opponentId, deckChoice);
      setGames([newGame, ...games]);
      setActiveTab('games');
    } catch (error) {
      console.error('Error creating game:', error);
    }
  };

  // Updated: Accept game with deck selection
  const handleAcceptGame = async (gameId: number, deckChoice: DeckChoice) => {
    try {
      await ddApi.acceptGameInvite(gameId, deckChoice);
      await loadDashboardData(); // Refresh games
    } catch (error) {
      console.error('Error accepting game:', error);
    }
  };

  const handleJoinGame = (gameId: number) => {
    navigate(`/game/${gameId}`);
  };

const handleGetStarterPack = async () => {
  if (!user || gettingStarterPack) return;
  
  try {
    setGettingStarterPack(true);
    setStarterPackMessage(null);
    
    const result = await ddApi.getStarterPack(user.id);
    
    if (!result.success) {
      setStarterPackMessage({
        type: 'error',
        text: result.error || 'Failed to get starter pack'
      });
      return;
    }
    
    // Success!
    setStarterPackMessage({
      type: 'success',
      text: result.message || 'Starter pack received!'
    });
    
    // Refresh dashboard data to show new cards and decks
    await loadDashboardData();
    
    // Clear success message after 5 seconds
    setTimeout(() => setStarterPackMessage(null), 5000);
    
  } catch (error) {
    console.error('Error getting starter pack:', error);
    setStarterPackMessage({
      type: 'error',
      text: 'Network error - please try again'
    });
  } finally {
    setGettingStarterPack(false);
  }
};

const needsStarterPack = ddApi.needsStarterPack(playerCards);


  // Digital Duel Game Guide Component
  const DigitalDuelGuide: React.FC = () => {
    return (
      <div className="bg-gray-800 p-4 rounded-lg mb-6">
        <h3 className="text-lg font-semibold mb-3 text-blue-400">How to Play Digital Duel</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-blue-300">🃏 Deck Building</p>
            <p className="text-gray-300">Build up to 3 custom decks with your cards. Each deck can have up to 10 cards for strategic gameplay. Synergy suggestions are presented during the build. Decks allocated to active or pending games are locked.</p>
          </div>
          
          <div>
            <p className="font-medium text-blue-300">Challenge Players</p>
            <p className="text-gray-300">Choose your deck when challenging opponents. Players can be from your faction or rival factions. Multiple games can be played at once.</p>
          </div>
          
          <div>
            <p className="font-medium text-blue-300">Card Collection</p>
            <p className="text-gray-300">Unlock cards by playing SO games to earn Compute Cycles to then purchase cards in the shop. View your collection and discover new cards to earn.</p>
          </div>
          
          <div>
            <p className="font-medium text-blue-300">Turn-Based Battles</p>
            <p className="text-gray-300">Play cards from your selected deck to attack, defend, or use special abilities. Each player starts with 20 health.</p>
          </div>
          
          <div>
            <p className="font-medium text-blue-300">Card Types</p>
            <p className="text-gray-300">⚔️ Offensive cards deal damage, 🛡️ Defensive cards provide protection, 🔧 Utility cards heal or provide bonuses.</p>
          </div>
          
          <div>
            <p className="font-medium text-blue-300">Win Conditions</p>
            <p className="text-gray-300">Reduce your opponent's health to 0 to win. Strategic deck building and card combinations are key to victory.</p>
          </div>
        </div>
        
        <div className="mt-3 pt-3 border-t border-gray-700">
          <p className="text-xs text-yellow-300">
            💎 <strong>Deck Strategy:</strong> Build multiple decks for different strategies! Empty decks will auto-select from your other decks.
          </p>
        </div>
		        <div className="mt-3 pt-3 border-t border-gray-700">
          <p className="text-xs text-green-300">
            💎 <strong>Card Designs:</strong> Get in touch with your card ideas, if they are added to the game you will get the card for free!.
          </p>
        </div>
      </div>
    );
  };

  if (!user || !profile) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
    {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-blue-400 flex items-baseline">
              Digital Duel 
              <span className="text-sm font-normal text-gray-400 ml-2">v0.8</span>
            </h1>
            <p className="text-gray-300">
              Welcome, {profile.username} • 
              <span className={`ml-2 ${profile.faction_id === 1 ? 'text-blue-400' : 'text-red-400'}`}>
                {profile.faction_id === 1 ? 'Defender' : 'Attacker'}
              </span>
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <a
                href="https://threat-command.shieldedonion.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                🎮 Play Threat Command
              </a>
              <p className="text-xs text-gray-400 mt-1">Earn CC for Cards</p>
            </div>

            <div className="bg-gray-800 p-4 rounded-lg text-center">
              <a href='https://ko-fi.com/K3K81BYK1R' target='_blank' rel='noreferrer'>
                <img 
                  src='https://storage.ko-fi.com/cdn/kofi6.png?v=6' 
                  alt='Buy Me a Coffee at ko-fi.com' 
                  className="inline-block h-8 w-auto" 
                />
              </a>
              <p className="text-xs text-gray-500 mt-1">Support this project</p>
            </div>
          </div>
      
          <div className="text-right">
            <div className="text-right">
              <p className="text-yellow-400 font-bold">{profile.cc_balance} CC</p>
              <p className="text-sm text-gray-400">Streak: {profile.consecutive_days_played} days</p>
              <p className="text-sm text-green-400">
                {games.filter(g => g.game_status === 'completed' && g.winner_id === user?.id).length} wins / {games.filter(g => g.game_status === 'completed').length} games
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
 {[
  { id: 'games', label: 'My Games', count: games.length },
  { id: 'collection', label: 'Card Collection', count: playerCards.length },
  { id: 'deck-builder', label: '🃏 Deck Builder', count: null },
  { id: 'shop', label: '🛒 Card Shop', count: null },
  { id: 'players', label: 'Find Opponents', count: availablePlayers.length },
  { id: 'leaderboard', label: '🏆 Leaderboard', count: null }
].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'deck-builder') {
                    navigate('/deck-builder');
                  } else {
                    setActiveTab(tab.id as any);
                  }
                }}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-300 hover:text-white hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.count !== null && (
                  <span className="ml-2 bg-gray-600 text-xs px-2 py-1 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-400">Loading dashboard...</div>
          </div>
) : (
          <>
            {/* Starter Pack Alert */}
            {needsStarterPack && (
              <div className="mb-6 bg-gradient-to-r from-green-600 to-green-500 rounded-lg p-6 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">🎁 Get Your Free Starter Pack!</h3>
                    <p className="text-green-100">You need cards to play. Claim your free starter pack with 8 cards and 2 pre-built decks!</p>
                  </div>
                  <button
                    onClick={handleGetStarterPack}
                    disabled={gettingStarterPack}
                    className="bg-white hover:bg-gray-100 disabled:bg-gray-300 text-green-600 px-8 py-4 rounded-lg font-bold text-lg shadow-lg transform transition-all duration-200 hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed whitespace-nowrap ml-4"
                  >
                    {gettingStarterPack ? 'Getting Cards...' : 'Claim Now'}
                  </button>
                </div>
                {starterPackMessage && (
                  <div className={`mt-4 px-4 py-2 rounded ${
                    starterPackMessage.type === 'success' 
                      ? 'bg-white text-green-600' 
                      : 'bg-red-100 text-red-600'
                  }`}>
                    {starterPackMessage.text}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'games' && (
              <>
                <DigitalDuelGuide />
                <GameList 
                  games={games}
                  currentUserId={user.id}
                  onJoinGame={handleJoinGame}
                  onAcceptGame={handleAcceptGame}
				    onRefresh={loadDashboardData} 
					  needsStarterPack={needsStarterPack}
  onGetStarterPack={handleGetStarterPack}
  gettingStarterPack={gettingStarterPack}
  starterPackMessage={starterPackMessage}
                />
              </>
            )}

            {activeTab === 'shop' && (
              <CardShop onPurchaseComplete={loadDashboardData} />
            )}

            {activeTab === 'collection' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">Your Card Collection</h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => navigate('/deck-builder')}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded font-medium transition-colors flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      🃏 Build Decks
                    </button>
                    <button
                      onClick={() => navigate('/collection')}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium transition-colors flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      View Full Collection
                    </button>
                  </div>
                </div>
                
                {playerCards.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-400 mb-4">No cards unlocked yet</p>
                    <p className="text-sm text-gray-500">
                      Complete activities in other SO games to unlock cards!
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-gray-800 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-blue-400">{playerCards.length}</div>
                        <div className="text-sm text-gray-400">Total Cards</div>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-red-400">
                          {playerCards.filter(c => c.card_type === 'offensive').length}
                        </div>
                        <div className="text-sm text-gray-400">⚔️ Offensive</div>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-blue-400">
                          {playerCards.filter(c => c.card_type === 'defensive').length}
                        </div>
                        <div className="text-sm text-gray-400">🛡️ Defensive</div>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-green-400">
                          {playerCards.filter(c => c.card_type === 'utility').length}
                        </div>
                        <div className="text-sm text-gray-400">🔧 Utility</div>
                      </div>
                    </div>

                    {/* Preview of Recent/Featured Cards */}
                    <div className="mb-4">
                      <h3 className="text-lg font-medium text-gray-300 mb-3">
                        Recent Cards 
                        <span className="text-sm text-gray-500 ml-2">
                          (showing {Math.min(8, playerCards.length)} of {playerCards.length})
                        </span>
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {playerCards.slice(0, 8).map(card => (
                        <CardDisplay key={card.id} card={card} showDetails />
                      ))}
                    </div>

                    {/* Show More Button if there are more cards */}
                    {playerCards.length > 8 && (
                      <div className="text-center mt-6">
                        <button
                          onClick={() => navigate('/collection')}
                          className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                        >
                          View All {playerCards.length} Cards
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

 {activeTab === 'players' && (
              <PlayerList 
                players={availablePlayers}
                currentUserFaction={profile.faction_id}
                onChallenge={handleCreateGame}
              />
            )}

            {activeTab === 'leaderboard' && (
              <Leaderboard />
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;